import type { GroupShow, ListStateEvent } from '../../types'
import type { TrendingResult } from '../../types/mfc'
import { Settings } from '../../types/state'
import {
    endMemberRoomsSession,
    onMemberRoomCreateReply,
    onMemberRoomPacket,
    startMemberRoomsSession,
} from '../actions/members'
import { updateSettingsAndEmit } from '../actions/settings'
import {
    AppStateType,
    ChatAction,
    EventType,
    HEALTH,
    ListAction,
    MembersAction,
    SearchAction,
    SearchEvent,
    UserAction,
    UserEvent,
} from '../constants/enums'
import { logout } from '../mfcLogic/auth'
import { getLens } from '../mfcLogic/lens'
import { toMemberWebcam } from '../mfcLogic/memberWebcams'
import {
    FCTYPE_ANNOUNCE,
    FCTYPE_INBOX,
    FCTYPE_MYUCR,
    FCTYPE_PUBLISH,
    FCTYPE_ROOMDATA,
    FCTYPE_ROOMHELPER,
    FCTYPE_RULEVIOLATION,
    FCTYPE_UCR,
} from '../mfcLogic/mfcConstants'
import { trackAffiliates } from '../mfcLogic/trackAffiliates'
import { eraseCookie, getCookie } from '../utilities/cookies'
import {
    onALERTS,
    onANNOUNCE,
    onINBOX,
    onMessages,
    onPUBLISH,
    onROOMDATA,
    onROOMHELPER,
    onRULEVIOLATION,
} from './handlers'
import { idToName, idToSlug, sessionToID, slugToID } from './internal/users'
import { openRequiredChangeDialog } from './modals/state.dialog'
import { pubSubSearch, pubSubUsers } from './pubSub'
import { onChanges } from './resolveUserUpdates'
import {
    setMemberWebcams,
    setMemberWebcamSelectionMask,
    setMemberWebcamsError,
    setMyMemberWebcam,
} from './state.members'

import { updateAppState } from './state.app'
import { mergeArchiveDataState } from './state.archives'
import { healthStateMutators } from './state.health'
import { deleteLists, modifyLists, resetLists } from './state.lists'
import {
    setRoomGuestCount,
    setRoomInsertParticipantState,
    setRoomJoinDeniedState,
    setRoomParticipantsState,
    setRoomRemoveParticipantState,
} from './state.rooms'
import { AppRouter } from './state.router'
import {
    buildTabbedResults,
    SearchResultUser,
    setSearchLoading,
    updateSearchResults,
} from './state.search'
import { onUserAction } from './state.users'

const fcsMutatorMap = {
    [FCTYPE_UCR]: onMemberRoomPacket,
    [FCTYPE_MYUCR]: onMemberRoomCreateReply,
    [FCTYPE_RULEVIOLATION]: onRULEVIOLATION,
    [FCTYPE_ROOMHELPER]: onROOMHELPER,
    [FCTYPE_ANNOUNCE]: onANNOUNCE,
    [FCTYPE_ROOMDATA]: onROOMDATA,
    [FCTYPE_PUBLISH]: onPUBLISH,
    [FCTYPE_INBOX]: onINBOX,
}

const listsMutatorMap = {
    [ListAction.DELETE]: deleteLists,
    [ListAction.RESET]: resetLists,
    [ListAction.MODIFY]: modifyLists,
}

const onArchive = (event) => {
    if (!event?.data) {
        console.error('[onArchive]: Event is missing data.')
        return
    }

    const { data } = event
    mergeArchiveDataState(data)
}

const chatMutatorMap = {
    [ChatAction.ARCHIVE]: onArchive,
}

const usersMutatorMap = {
    [UserAction.SET_MODELS_REQUESTED]: onUserAction(UserEvent.MODELS_REQUESTED),
    [UserAction.SOCKET_LOOKUP_USER]: onUserAction(UserEvent.SOCKET_LOOKUP),
    [UserAction.SET_MODELS_LOADED]: onUserAction(UserEvent.MODELS_LOADED),
    [UserAction.SEARCH_NOTES]: onUserAction(UserEvent.SEARCH_NOTES),
    [UserAction.GET_ALL_USERS]: onUserAction(UserEvent.ALL_USERS),
    [UserAction.DELETE_NOTE]: onUserAction(UserEvent.DELETE_NOTE),
    [UserAction.SAVE_NOTE]: onUserAction(UserEvent.SAVE_NOTE),
    [UserAction.LOOKUP_USER]: onUserAction(UserEvent.LOOKUP),
    [UserAction.ADD_NOTE]: onUserAction(UserEvent.ADD_NOTE),
    [UserAction.MESSAGES]: onMessages,
    [UserAction.CHANGES]: onChanges,
    [UserAction.ALERTS]: onALERTS,
}

const indexStateMutators = (event: MessageEvent<any>) => {
    const { indices } = event.data
    indices.forEach(({ session, slug, name, id }) => {
        sessionToID[session] = id
        slugToID[slug] = id
        idToSlug[id] = slug
        idToName[id] = name

        if (slug === 'me') {
            pubSubUsers.emit(UserEvent.ME_ID, id)
        }
    })
}

const listsStateMutators = (event: ListStateEvent) => {
    const { action } = event.data
    const mutator = listsMutatorMap[action]
    mutator?.(event.data)
}

const chatStateMutators = (event: ListStateEvent) => {
    const { action } = event.data
    const mutator = chatMutatorMap[action]
    mutator?.(event.data)
}

const fcsPacketHandlers = (event: MessageEvent<any>) => {
    const { action, data } = event.data
    const mutator = fcsMutatorMap[action]
    mutator?.(data)
}

interface SearchStateData {
    data: {
        action: SearchAction
        groupShows: Array<GroupShow>
        list: string
        query: string
        results: Array<SearchResultUser>
        search: string
        trendingResults: TrendingResult
    }
}

const searchStateMutators = (event: MessageEvent<SearchStateData>) => {
    const { data } = event.data
    const { results = [], search } = data
    const resolvedResults = results.map((result) => {
        const { entry } = result
        return {
            ...result,
            entry: {
                ...entry,
                id: Number(entry.id),
            },
        }
    })
    const action = data.action
    switch (action) {
        case SearchAction.SEARCH_LIST: {
            const { list, query } = data
            pubSubSearch.emit(
                `${SearchEvent.RESULTS_LIST}::${list}::${query}`,
                resolvedResults,
                true,
            )
            break
        }
        case SearchAction.SEARCH: {
            updateSearchResults({
                data: buildTabbedResults(resolvedResults, search),
            })
            setSearchLoading(false)
            break
        }
    }
}

const settingsStateMutators = (event: MessageEvent<any>) => {
    const { data } = event.data
    const { payload: settings } = data
    settings.forEach(
        <T extends keyof Settings, U extends keyof Settings[T]>({
            type,
            key,
            value,
        }: {
            type: T
            key: U
            value: Settings[T][U]
        }) => {
            updateSettingsAndEmit({ type, key, value })
        },
    )
}

const usersStateMutators = (event: MessageEvent<any>) => {
    const { action, data } = event.data

    const mutator = usersMutatorMap[action]

    mutator?.(data)
}

const loginStateMutators = (event: MessageEvent<any>) => {
    const { payload: nm, arg2: uid, to: sid, payload, error } = event.data

    getLens()?.Login(sid, uid, nm, getCookie('user_lv'))

    trackAffiliates(sid, nm)

    if (uid !== 0) {
        eraseCookie('lmvt')
        eraseCookie('lmv')
    }

    if (!error) startMemberRoomsSession()

    const requireModification = payload?.require_account_modification >= 1
    if (requireModification) {
        const requireUsernameChange =
            payload?.require_account_modification === 1 ||
            payload?.require_account_modification === 3
        const requirePasswordChange =
            payload?.require_account_modification === 2 ||
            payload?.require_account_modification === 3
        updateAppState(AppStateType.APP, {
            requiresAccountChange: true,
            requireUsernameChange,
            requirePasswordChange,
        })
        // due to loading strategy and floater logic
        // we need to invoke a dialog due to rendering issues
        // with the messages page
        const visiblePage = AppRouter.route.visiblePage
        const isIndividualPMPage = visiblePage === 'message'
        if (isIndividualPMPage) openRequiredChangeDialog()
        return
    }
    if (!error) {
        return
    }
    console.warn("Looks like the user's passcode has changed! Reloading.")
    logout()
    location.reload()
}

const participantsStateMutators = (event) => {
    const participants = event.data.data

    participants.forEach((data) => {
        const { participants, guestCount, removeID, insert, roomID } = data

        if (typeof data.joinDenied === 'boolean') {
            setRoomJoinDeniedState(data.joinDenied, roomID)
            return
        }

        if (participants) {
            setRoomParticipantsState(participants, roomID)
            return
        }

        if (insert) {
            setRoomInsertParticipantState(insert, roomID)
            return
        }

        if (removeID) {
            setRoomRemoveParticipantState(removeID, roomID)
            return
        }

        if (guestCount !== undefined) {
            setRoomGuestCount(guestCount, roomID)
            return
        }
    })
}

const onMemberWebcams = ({ webcams, myWebcam, webcamSelectionMask }) => {
    if (typeof webcamSelectionMask === 'number') {
        setMemberWebcamSelectionMask(webcamSelectionMask)
    }
    setMemberWebcams(webcams.map(toMemberWebcam))
    setMyMemberWebcam(myWebcam && toMemberWebcam(myWebcam))
}

const membersMutatorMap = {
    [MembersAction.WEBCAMS]: onMemberWebcams,
    [MembersAction.WEBCAMS_ERROR]: setMemberWebcamsError,
}

const membersStateMutators = (event: MessageEvent<any>) => {
    const { action, data } = event.data
    const mutator = membersMutatorMap[action]
    mutator?.(data)
}

const connectionStateMutators = (event: MessageEvent<any>) => {
    healthStateMutators(event)
    const { status } = event.data
    if (status === HEALTH.DISCONNECTED || status === HEALTH.ERROR) {
        endMemberRoomsSession()
    }
}

/** handle packet events from the worker: state mutations and side effects */
export const MUTATOR_MAP = {
    [EventType.FCS]: fcsPacketHandlers,
    [EventType.HEALTH]: connectionStateMutators,
    [EventType.INDEX]: indexStateMutators,
    [EventType.LISTS]: listsStateMutators,
    [EventType.SEARCH]: searchStateMutators,
    [EventType.SETTINGS]: settingsStateMutators,
    [EventType.PARTICIPANTS]: participantsStateMutators,
    [EventType.USERS]: usersStateMutators,
    [EventType.FCTYPE_LOGIN]: loginStateMutators,
    [EventType.CHAT]: chatStateMutators,
    [EventType.MEMBERS]: membersStateMutators,
}
